import { useLocation } from "wouter";
import { CheckCircle2, HelpCircle, ArrowRight } from "lucide-react";

const asset = (file: string) => `${import.meta.env.BASE_URL}landing/${file}`;

const OS_GREEN = "#21B155";
const OS_SLATE = "#384C5A";
const OS_SLATE_DARK = "#2A3942";

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  titleColor?: string;
  isHighlighted?: boolean;
  linkLabel?: string;
}

function FeatureCard({
  icon,
  title,
  description,
  titleColor = "text-[#21B155]",
  isHighlighted = false,
  linkLabel,
}: FeatureCardProps) {
  return (
    <div
      className={`p-6 rounded-xl transition-all duration-200 hover:shadow-md ${
        isHighlighted
          ? "border-2 border-[#384C5A]/20 bg-[#384C5A]/5"
          : "border border-gray-100 bg-white"
      }`}
    >
      <div className="mb-3">{icon}</div>
      <h3 className={`text-sm font-bold mb-1.5 ${titleColor}`}>{title}</h3>
      <p className="text-xs text-gray-600 leading-relaxed">{description}</p>
      {linkLabel && (
        <span className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-[#384C5A]">
          {linkLabel} <ArrowRight className="w-3 h-3" />
        </span>
      )}
    </div>
  );
}

export function Landing() {
  const [, setLocation] = useLocation();

  return (
    <div className="min-h-screen flex flex-col bg-white font-sans">
      {/* ===== HEADER ===== */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50 shadow-sm">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-3 flex items-center justify-between">
          {/* Co-brand lockup: OpenServe and Mooya as partners, joined by a divider line */}
          <div className="flex items-center gap-4">
            <img
              src={asset("OpenServeLogo.png")}
              alt="OpenServe"
              className="h-12 lg:h-14 w-auto object-contain"
            />
            <div className="w-px h-10 bg-gray-200" />
            <img
              src={asset("MooyaLogo.png")}
              alt="Mooya Wireless"
              className="h-11 lg:h-12 w-auto object-contain"
            />
          </div>

          <div className="hidden md:flex items-center gap-8">
            <img
              src={asset("TelkomLogo.png")}
              alt="Telkom South Africa"
              className="h-9 lg:h-10 w-auto object-contain"
            />
            <div className="w-px h-9 bg-gray-200" />
            <img
              src={asset("XnextLogo.jpg")}
              alt="XNEXT"
              className="h-9 lg:h-10 w-auto object-contain"
            />
          </div>

          <button
            type="button"
            onClick={() => setLocation("/sign-in")}
            className="flex items-center gap-2 text-gray-500 hover:text-[#21B155] transition-colors"
          >
            <HelpCircle className="w-4 h-4" />
            <span className="text-sm font-medium hidden sm:inline">Help</span>
          </button>
        </div>
      </header>

      {/* ===== HERO SECTION ===== */}
      <section className="bg-white">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10 lg:py-16">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-10">
            {/* Left: Text on clean white, far left */}
            <div className="w-full lg:w-[22%] lg:shrink-0">
              <span
                className="inline-block text-[11px] font-bold tracking-[0.18em] uppercase"
                style={{ color: OS_SLATE }}
              >
                OpenServe · in partnership with Mooya
              </span>
              <h1 className="mt-2 text-[1.7rem] lg:text-[2.2rem] font-bold leading-tight text-gray-900">
                Agent Portal
              </h1>
              <p className="mt-2 text-lg lg:text-xl font-semibold text-[#21B155]">
                The Unified Fibre Network
              </p>
              <p className="mt-5 text-sm lg:text-base text-gray-600 leading-relaxed max-w-md">
                One network. More possibilities. The unified agent experience for
                OpenServe, Telkom South Africa and XNEXT.
              </p>
              <div className="mt-6 space-y-3">
                {[
                  "One login. The full OpenServe network.",
                  "Real-time updates and insights.",
                  "Grow your business, together.",
                ].map((item, i) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2
                      className="w-5 h-5 flex-shrink-0"
                      fill={i % 2 === 0 ? OS_GREEN : OS_SLATE}
                      stroke="white"
                    />
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: full-width photo with the sign-in card to the right */}
            <div className="w-full lg:flex-1 lg:min-w-0 flex flex-col lg:flex-row lg:items-start">
              {/* Hero photo (real Mooya/OpenServe agents, OpenServe-green van) with an OpenServe signage decal */}
              <div className="relative w-full lg:flex-1 lg:min-w-0">
                <img
                  src={asset("hero-openserve-dealer-v4.png")}
                  alt="Mooya agent and OpenServe technician beside an OpenServe-green service van"
                  className="w-full aspect-[16/9] object-cover rounded-2xl shadow-xl ring-1 ring-gray-100"
                  style={{ objectPosition: "center" }}
                />
                {/* OpenServe fleet decal sitting on the cleared green van side panel */}
                <div
                  className="absolute"
                  style={{ left: "63%", top: "33%", width: "30%" }}
                >
                  <div className="bg-white rounded-xl shadow-lg ring-1 ring-black/5 px-5 py-4 flex flex-col items-center">
                    <img
                      src={asset("OpenServeLogo.png")}
                      alt="OpenServe"
                      className="w-full h-auto object-contain"
                    />
                    <span className="mt-1.5 text-[10px] lg:text-xs font-semibold tracking-wide text-gray-400">
                      Fibre Network Partner
                    </span>
                  </div>
                </div>
              </div>

              {/* Sign-in entry card */}
              <div className="w-full max-w-[320px] mx-auto mt-6 lg:mt-0 lg:mx-0 lg:w-[270px] lg:shrink-0 lg:ml-8 relative z-10">
                <div className="bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
                  <div className="h-1.5 w-full" style={{ backgroundColor: OS_SLATE }} />
                  <div className="p-6">
                    <h2 className="text-xs text-gray-600">Welcome to the</h2>
                    <p className="text-lg font-bold text-[#21B155] mt-0.5">
                      OpenServe Agent Portal
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Sign in to your account to continue.
                    </p>

                    <div className="mt-5 space-y-3">
                      <button
                        type="button"
                        onClick={() => setLocation("/sign-in")}
                        className="w-full h-10 bg-[#21B155] hover:bg-[#178045] text-white font-semibold rounded-lg transition-all duration-200 active:scale-[0.97] text-sm"
                      >
                        Sign In
                      </button>

                      <div className="relative py-1">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-gray-200" />
                        </div>
                        <div className="relative flex justify-center text-xs">
                          <span className="bg-white px-3 text-gray-400">or</span>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => setLocation("/sign-up")}
                        className="w-full h-10 border-2 font-semibold rounded-lg transition-all duration-200 active:scale-[0.97] text-sm"
                        style={{ borderColor: OS_SLATE, color: OS_SLATE }}
                      >
                        Create New Account
                      </button>
                    </div>

                    <p className="mt-5 text-[10px] text-center text-gray-400 leading-relaxed">
                      By continuing, you agree to our{" "}
                      <span style={{ color: OS_GREEN }}>Terms of Use</span> and{" "}
                      <span style={{ color: OS_GREEN }}>Privacy Policy</span>.
                    </p>
                    <p className="mt-3 text-[10px] text-center text-gray-300">
                      OpenServe network · powered by Mooya
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURES SECTION ===== */}
      <section className="bg-[#FAFAFA] border-t border-gray-100">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10 lg:py-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            <FeatureCard
              icon={
                <svg viewBox="0 0 40 40" className="w-10 h-10">
                  <circle cx="20" cy="14" r="5" fill="none" stroke={OS_GREEN} strokeWidth="2" />
                  <circle cx="12" cy="28" r="4" fill="none" stroke={OS_GREEN} strokeWidth="2" />
                  <circle cx="28" cy="28" r="4" fill="none" stroke={OS_GREEN} strokeWidth="2" />
                  <line x1="20" y1="19" x2="12" y2="24" stroke={OS_GREEN} strokeWidth="1.5" />
                  <line x1="20" y1="19" x2="28" y2="24" stroke={OS_GREEN} strokeWidth="1.5" />
                </svg>
              }
              title="Unified Access"
              description="Manage OpenServe fibre, Telkom South Africa and XNEXT from one portal."
              titleColor="text-[#21B155]"
            />
            <FeatureCard
              icon={
                <svg viewBox="0 0 40 40" className="w-10 h-10">
                  <rect x="8" y="22" width="5" height="12" fill={OS_SLATE} rx="1" />
                  <rect x="17" y="14" width="5" height="20" fill={OS_SLATE} rx="1" />
                  <rect x="26" y="8" width="5" height="26" fill={OS_SLATE} rx="1" />
                </svg>
              }
              title="Real-time Insights"
              description="Track performance, sales and activations with real-time dashboards."
              titleColor="text-[#384C5A]"
            />
            <FeatureCard
              icon={
                <svg viewBox="0 0 40 40" className="w-10 h-10">
                  <rect x="10" y="8" width="20" height="26" rx="2" fill="none" stroke={OS_GREEN} strokeWidth="2" />
                  <line x1="14" y1="15" x2="26" y2="15" stroke={OS_GREEN} strokeWidth="1.5" />
                  <line x1="14" y1="20" x2="26" y2="20" stroke={OS_GREEN} strokeWidth="1.5" />
                  <line x1="14" y1="25" x2="22" y2="25" stroke={OS_GREEN} strokeWidth="1.5" />
                </svg>
              }
              title="Streamlined Operations"
              description="Access tools, resources and support to simplify your day-to-day operations."
              titleColor="text-[#21B155]"
            />
            <FeatureCard
              icon={
                <svg viewBox="0 0 40 40" className="w-10 h-10">
                  <circle cx="14" cy="16" r="5" fill="none" stroke={OS_SLATE} strokeWidth="2" />
                  <circle cx="26" cy="16" r="5" fill="none" stroke={OS_SLATE} strokeWidth="2" />
                  <path d="M6 32 Q14 24 22 28 Q30 32 34 28" fill="none" stroke={OS_SLATE} strokeWidth="1.5" />
                </svg>
              }
              title="Grow Together"
              description="More products. More opportunities. Stronger together."
              titleColor="text-[#384C5A]"
            />
            <FeatureCard
              icon={
                <svg viewBox="0 0 40 40" className="w-10 h-10">
                  <circle cx="20" cy="20" r="12" fill="none" stroke={OS_SLATE} strokeWidth="2" />
                  <path d="M20 14 L20 22" stroke={OS_SLATE} strokeWidth="2" strokeLinecap="round" />
                  <circle cx="20" cy="26" r="1.5" fill={OS_SLATE} />
                </svg>
              }
              title="Need Assistance?"
              description="Our support team is here to help."
              titleColor="text-[#384C5A]"
              isHighlighted
              linkLabel="Contact Support"
            />
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="text-white" style={{ backgroundColor: OS_SLATE_DARK }}>
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-sm font-medium flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: OS_GREEN }} />
            Proudly South African
          </span>
          <p className="text-xs text-white/60 text-center">
            © 2026 · OpenServe agent network, powered by Mooya Wireless (Pty) Ltd.
          </p>
          <div className="flex items-center gap-4">
            <span className="text-xs text-white/70">Terms of Use</span>
            <span className="text-white/30">|</span>
            <span className="text-xs text-white/70">Privacy Policy</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
